import React from "react";
import moment from "moment";
import { theme, fmtRelative } from "../theme";
import { useCurrencyFormatter } from "../../../../Components/Hooks/useCurrencyFormatter";
import MoneyDisplay from "./MoneyDisplay";

const cellStyle = {
  background: theme.surface,
  padding: "16px 20px",
};

const labelStyle = {
  fontSize: 11.5,
  fontWeight: 600,
  letterSpacing: "0.6px",
  textTransform: "uppercase",
  color: theme.textMuted,
};

const ContractMetricsGrid = ({ contract }) => {
  const { formatCurrency } = useCurrencyFormatter();
  const today = moment().startOf("day");

  const payments = contract?.payments || [];
  const units = contract?.units || [];

  const totalValue = contract?.total_contract_value || 0;
  const totalCashed = contract?.total_cashed || 0;
  const remainingBalance = contract?.remaining_balance ?? Math.max(totalValue - totalCashed, 0);
  const percentCollected = totalValue > 0 ? Math.round((totalCashed / totalValue) * 100) : 0;

  const totalSqm = units.reduce((sum, u) => sum + (parseFloat(u.net_sqm) || 0), 0);
  const openPayments = payments.filter((p) => !p.cashed);

  const overdue = openPayments.filter((p) => p.booking_date && moment(p.booking_date).isBefore(today));
  const overdueTotal = overdue.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

  const nextUpcoming = openPayments
    .filter((p) => p.booking_date && !moment(p.booking_date).isBefore(today))
    .sort((a, b) => moment(a.booking_date) - moment(b.booking_date))[0];

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
        gap: 1,
        background: theme.border,
        border: `1px solid ${theme.border}`,
        borderRadius: 14,
        overflow: "hidden",
        marginBottom: 28,
      }}
    >
      <div style={cellStyle}>
        <div style={labelStyle}>Contract Value</div>
        <div style={{ marginTop: 6 }}>
          <MoneyDisplay eurAmount={totalValue} ronAmount={contract?.total_contract_value_ron} />
        </div>
        <div style={{ fontSize: 12.5, color: theme.textMuted, marginTop: 3 }}>
          {units.length} unit{units.length === 1 ? "" : "s"}
          {totalSqm > 0 ? ` · ${totalSqm} sq.m` : ""}
        </div>
      </div>

      <div style={cellStyle}>
        <div style={labelStyle}>Collected</div>
        <div style={{ marginTop: 6 }}>
          <MoneyDisplay
            eurAmount={totalCashed}
            ronAmount={contract?.total_cashed_ron}
            color={theme.success}
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 9 }}>
          <div style={{ flex: 1, height: 5, borderRadius: 3, background: theme.neutralBadgeBg, overflow: "hidden" }}>
            <div
              style={{
                height: "100%",
                width: `${percentCollected}%`,
                background: theme.successBar,
                borderRadius: 3,
              }}
            ></div>
          </div>
          <span style={{ fontSize: 12, fontWeight: 600, color: theme.textSecondary }}>{percentCollected}%</span>
        </div>
      </div>

      <div style={cellStyle}>
        <div style={labelStyle}>Outstanding</div>
        <div style={{ marginTop: 6 }}>
          <MoneyDisplay eurAmount={remainingBalance} ronAmount={contract?.remaining_balance_ron} />
        </div>
        <div style={{ fontSize: 12.5, color: theme.textMuted, marginTop: 3 }}>
          {openPayments.length} of {payments.length} payments open
        </div>
      </div>

      <div style={cellStyle}>
        <div style={labelStyle}>{overdue.length ? "Overdue" : "Next Due"}</div>
        <div style={{ marginTop: 6 }}>
          {overdue.length ? (
            <MoneyDisplay
              eurAmount={overdueTotal}
              ronAmount={overdue.reduce((sum, p) => sum + (p.amount_ron || 0), 0) || null}
              color={theme.dangerText}
            />
          ) : nextUpcoming ? (
            <span style={{ fontSize: 23, fontWeight: 650, fontVariantNumeric: "tabular-nums" }}>
              {moment(nextUpcoming.booking_date).format("D MMM YYYY")}
            </span>
          ) : (
            <span style={{ fontSize: 23, fontWeight: 650 }}>None scheduled</span>
          )}
        </div>
        <div style={{ fontSize: 12.5, color: theme.textSecondary, marginTop: 3 }}>
          {overdue.length
            ? overdue.length === 1
              ? `${overdue[0].type} · ${fmtRelative(today.diff(moment(overdue[0].booking_date), "days"))} late`
              : `across ${overdue.length} payments`
            : nextUpcoming
            ? `${formatCurrency(nextUpcoming.amount, "EUR")} · in ${fmtRelative(
                moment(nextUpcoming.booking_date).diff(today, "days")
              )}`
            : "Balance has no due date"}
        </div>
      </div>
    </div>
  );
};

export default ContractMetricsGrid;
